import Grid from '@mui/material/Grid';
import { useCallback, useState } from 'react';
import { fireOnce, logExposure } from 'src/analytics';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import { Wrapper } from '../Completion/components/RichEndings';
import ErrorContainer from '../ErrorContainer';
import ResponsiveSurface from '../ResponsiveSurface';
import {
  trackSupplierConfirmationOfferingClicked,
  trackSupplierConfirmationOfferingViewed,
} from './analytics';
import StripeProvider from './components/Checkout/StripeProvider';
import DoctorConfirmationEntryCard from './components/EntryCard';
import DoctorConfirmationForm from './components/Form';
import { DoctorConfirmationContext } from './useDoctorConfirmationContext';
import useIsDoctorConfirmationEnabled from './useIsDoctorConfirmationEnabled';

import type { MiniInterviewType } from 'src/packages/polaris/types';

type Props = {
  interviewType: MiniInterviewType;
  interviewToken: string;
  isAlarmingDx: boolean;
};

const DoctorConfirmation = ({ interviewType, interviewToken, isAlarmingDx }: Props) => {
  const [showDoctorConfirmationForm, setShowDoctorConfirmationForm] = useState(false);
  const [isError, setIsError] = useState(false);
  const [customerId, setCustomerId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const { isEnabled, chargeAmount } = useIsDoctorConfirmationEnabled(interviewType);

  const closeDoctorConfirmationForm = useCallback(() => setShowDoctorConfirmationForm(false), []);

  const impressionRef = useImpressionTracking(() => {
    fireOnce(
      () =>
        logExposure({
          testName: 'dx confirmation price',
          testVariant: `${chargeAmount}`,
        }),
      `${interviewToken}-${chargeAmount}`,
    );

    trackSupplierConfirmationOfferingViewed({
      interviewToken,
      interviewType,
      chargeAmount,
    });
  });

  const handleClick = useCallback(() => {
    setShowDoctorConfirmationForm(true);
    trackSupplierConfirmationOfferingClicked({
      interviewToken,
      interviewType,
      chargeAmount,
    });
  }, [chargeAmount, interviewToken, interviewType]);

  const handleRestart = useCallback(() => {
    setIsError(false);
    setIsLoading(false);
    setCurrentQuestionIndex(0);
  }, []);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <Wrapper component="DxConfirmation">
        <Grid item ref={impressionRef} className="bp-relative">
          <DoctorConfirmationEntryCard chargeAmount={chargeAmount} onButtonClick={handleClick} />
        </Grid>
      </Wrapper>
      <ResponsiveSurface
        isOpen={showDoctorConfirmationForm}
        onClose={closeDoctorConfirmationForm}
        modalHeader="Get my visit results confirmed"
        backText="Back to results"
      >
        <DoctorConfirmationContext.Provider
          value={{
            setIsError,
            isError,
            setCustomerId,
            customerId,
            isLoading,
            setIsLoading,
            errorMessage,
            setErrorMessage,
            currentQuestionIndex,
            setCurrentQuestionIndex,
            firstName: '',
            email: '',
            chargeAmount,
          }}
        >
          {isError ? (
            <div className="bp-py-8">
              <ErrorContainer triggerRestart={handleRestart} />
            </div>
          ) : (
            <StripeProvider onError={setIsError}>
              <DoctorConfirmationForm
                onBack={closeDoctorConfirmationForm}
                interviewToken={interviewToken}
                interviewType={interviewType}
                isAlarmingDx={isAlarmingDx}
              />
            </StripeProvider>
          )}
        </DoctorConfirmationContext.Provider>
      </ResponsiveSurface>
    </>
  );
};

export default DoctorConfirmation;
